import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";

import { ownerStamp } from "../recordOwner";
import ModuleEditPermissions from "./ModuleEditPermissions";
import { isAdminRole, normaliseRole } from "../roles";
import {
  Wrench,
  Settings,
  ClipboardList,
  Plus,
  Edit2,
  Trash2,
  Image as ImageIcon,
  FileText,
  CheckCircle,
  Clock,
} from "lucide-react";
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from "../i18n.jsx";
import "../theme-palette.css";

// Takes the translate function rather than closing over one: it is used by two of the components
// in this file. The parameter is deliberately not named `t` -- see i18n/no-shadowed-t.
const statusWord = (tr, v) => tr(
  `maintenance.statuses.${String(v || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, v,
);

const PhotoThumbnail = ({ url }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (e) => {
    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    let left = rect.right + 10;
    let top = rect.top - 150 + (rect.height / 2);
    
    // Prevent clipping on the right edge
    if (left + 320 > window.innerWidth) {
      left = rect.left - 320;
    }
    // Prevent clipping on top/bottom
    if (top < 10) top = 10;
    if (top + 320 > window.innerHeight) top = window.innerHeight - 320;

    setPos({ left, top });
  };

  if (!url) return <span>-</span>;

  return (
    <>
      <div 
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovered(false)}
      >
        <a href={url} target="_blank" rel="noreferrer">
          <img 
            src={url} 
            alt="Thumbnail" 
            style={{ width: "32px", height: "32px", objectFit: "cover", borderRadius: "4px", border: "1px solid var(--color-border)" }} 
          />
        </a>
      </div>
      {isHovered && createPortal(
        <div style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          zIndex: 99999,
          backgroundColor: "var(--color-bg-card)",
          padding: "4px",
          borderRadius: "8px",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
          border: "1px solid var(--color-border)",
          pointerEvents: "none"
        }}>
          <img 
            src={url} 
            alt="Preview" 
            style={{ width: "300px", height: "300px", objectFit: "cover", borderRadius: "4px", display: "block" }} 
          />
        </div>,
        document.body
      )}
    </>
  );
};

const getTimestampStr = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}${String(d.getSeconds()).padStart(2, '0')}`;
};

// ----------------------------------------------------------------------
// Settings View
// ----------------------------------------------------------------------
const SettingsView = ({ activeSchoolId, settings, staff }) => {
  const { t } = useT();
  const { isMobile } = useOutletContext();
  const [maintenanceAdmins, setMaintenanceAdmins] = useState(settings?.selectedPersons || []);
  const [reporters, setReporters] = useState(settings?.reporters || []);
  
  const [adminSelect, setAdminSelect] = useState("");
  const [reporterSelect, setReporterSelect] = useState("");

  useEffect(() => {
    setMaintenanceAdmins(settings?.selectedPersons || []);
    setReporters(settings?.reporters || []);
  }, [settings]);

  const handleSave = async () => {
    try {
      await setDoc(
        doc(db, "schools", activeSchoolId, "metadata", "maintenance_settings"),
        {
          selectedPersons: maintenanceAdmins,
          reporters,
        },
        { merge: true },
      );
      alert(t('maintenance.settingsSaved'));
    } catch (err) {
      console.error(err);
      alert(t('maintenance.settingsSaveFailed'));
    }
  };

  const getStaffInitials = (email) => {
    if (email === "all") return "All Staff";
    const s = staff.find(x => x.email === email || x.id === email);
    if (!s) return email ? email.split('@')[0].toUpperCase().substring(0, 3) : '';
    if (s.initial) return s.initial;
    return `${s.firstName?.charAt(0) || ''}${s.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const addAdmin = () => {
    if (adminSelect && !maintenanceAdmins.includes(adminSelect)) {
      setMaintenanceAdmins([...maintenanceAdmins, adminSelect]);
      setAdminSelect("");
    }
  };

  const removeAdmin = (email) => {
    setMaintenanceAdmins(maintenanceAdmins.filter(a => a !== email));
  };

  const addReporter = () => {
    if (reporterSelect && !reporters.includes(reporterSelect)) {
      setReporters([...reporters, reporterSelect]);
      setReporterSelect("");
    }
  };

  const removeReporter = (email) => {
    setReporters(reporters.filter(a => a !== email));
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <div style={{ padding: isMobile ? "0.75rem" : "1.5rem", backgroundColor: "var(--color-bg-card)", borderRadius: isMobile ? 0 : "var(--radius-lg)", boxShadow: isMobile ? "none" : "var(--shadow-md)", border: "1px solid var(--color-border)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: "600" }}>{t('maintenance.accessControlSettings')}</h2>
          <button className="btn-primary" onClick={handleSave}>{t('maintenance.saveSettings')}</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem" }}>
          {/* Maintenance Admin */}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: "var(--color-text-main)" }}>{t('maintenance.maintenanceAdmin')}</label>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
              <select 
                style={{ flex: "0 0 25%", height: "40px", boxSizing: "border-box", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--color-border)", backgroundColor: "var(--color-bg-input)", color: "var(--color-text-main)" }}
                value={adminSelect}
                onChange={(e) => setAdminSelect(e.target.value)}
              >
                <option value="">{t('maintenance.selectStaffOption')}</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.email || s.id}>{getStaffInitials(s.email || s.id)}</option>
                ))}
              </select>
              <button type="button" className="btn-primary" style={{ flex: 1, padding: "0 1.5rem", height: "40px" }} onClick={addAdmin}>{t('common.add')}</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {maintenanceAdmins.map(admin => (
                <div key={admin} style={{ display: "flex", alignItems: "center", gap: "0.5rem", backgroundColor: "var(--color-bg-input)", border: "1px solid var(--color-border)", padding: "0.25rem 0.75rem", borderRadius: "20px", fontSize: "0.8rem", color: "var(--color-text-main)" }}>
                  <span>{getStaffInitials(admin)}</span>
                  <button type="button" onClick={() => removeAdmin(admin)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", color: "var(--color-text-muted)", padding: 0 }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Who can report? */}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: "var(--color-text-main)" }}>{t('maintenance.whoCanReport')}</label>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
              <select 
                style={{ flex: "0 0 25%", height: "40px", boxSizing: "border-box", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--color-border)", backgroundColor: "var(--color-bg-input)", color: "var(--color-text-main)" }}
                value={reporterSelect}
                onChange={(e) => setReporterSelect(e.target.value)}
              >
                <option value="">{t('maintenance.selectStaffOption')}</option>
                <option value="all">{t('maintenance.allStaff')}</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.email || s.id}>{getStaffInitials(s.email || s.id)}</option>
                ))}
              </select>
              <button type="button" className="btn-primary" style={{ flex: 1, padding: "0 1.5rem", height: "40px" }} onClick={addReporter}>{t('common.add')}</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {reporters.map(reporter => (
                <div key={reporter} style={{ display: "flex", alignItems: "center", gap: "0.5rem", backgroundColor: "var(--color-bg-input)", border: "1px solid var(--color-border)", padding: "0.25rem 0.75rem", borderRadius: "20px", fontSize: "0.8rem", color: "var(--color-text-main)" }}>
                  <span>{getStaffInitials(reporter)}</span>
                  <button type="button" onClick={() => removeReporter(reporter)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", color: "var(--color-text-muted)", padding: 0 }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// My Report View
// ----------------------------------------------------------------------
const MyReportView = ({
  activeSchoolId,
  currentUserProfile,
  userId,
  locations,
  reports,
  openNewReportTrigger,
  onNewReportReset,
  debugLog,
}) => {
  const { t } = useT();
  const { isMobile, closeSidebar } = useOutletContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ location: "", description: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (openNewReportTrigger > 0) {
      handleOpenNew();
      if (onNewReportReset) onNewReportReset();
    }
  }, [openNewReportTrigger, onNewReportReset]);

  // Shared dialog behaviour: retract the mobile sidebar (a fixed overlay that would otherwise sit on
  // top of the sheet), stop the page behind from scrolling, and close on Escape. Escape is ignored
  // mid-upload so a half-finished photo upload can't be abandoned by a stray keypress.
  useEffect(() => {
    if (!isModalOpen) return;
    if (isMobile) closeSidebar?.();

    const onKey = (e) => { if (e.key === 'Escape' && !uploading) setIsModalOpen(false); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isModalOpen, isMobile, closeSidebar, uploading]);

  const myReports = reports.filter(
    (r) => r.submit_by === (currentUserProfile?.initial || userId) || r.submit_by === userId
  );

  const handleEdit = (report) => {
    setEditingId(report.maintain_id);
    setFormData({ location: report.location, description: report.description });
    setFile(null);
    setIsModalOpen(true);
  };

  const handleOpenNew = () => {
    setEditingId(null);
    setFormData({ location: "", description: "" });
    setFile(null);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.location || !formData.description)
      return alert(t('maintenance.fillRequired'));

    setUploading(true);
    try {
      const recordId = editingId || `MNT-${Date.now()}`;
      let photoUrl = editingId
        ? myReports.find((r) => r.maintain_id === editingId)?.photoUrl || ""
        : "";

      if (file) {
        const ext = file.name.split('.').pop();
        const newFileName = `${getTimestampStr()}.${ext}`;
        const fileRef = ref(
          storage,
          `upload/schools/${activeSchoolId}/maintenance/${recordId}/${newFileName}`,
        );
        const snapshot = await uploadBytesResumable(fileRef, file);
        photoUrl = await getDownloadURL(snapshot.ref);
      }

      const updateData = {
        location: formData.location,
        description: formData.description,
        photoUrl,
        last_update: new Date()
          .toISOString()
          .replace("T", " ")
          .substring(0, 19),
      };

      if (editingId) {
        await updateDoc(
          doc(db, "schools", activeSchoolId, "maintenance_reports", recordId),
          updateData,
        );
      } else {
        const newRecord = {
          ...updateData,
          // The rules require the author on create -- submit_by holds an accountId, which a rule
          // cannot verify. See src/recordOwner.js.
          ...ownerStamp(),
          maintain_id: recordId,
          submit_date: updateData.last_update,
          submit_by: userId,
          status: "Submitted",
          handle_by: "",
          quotation_files: [],
          price: "",
        };
        await setDoc(
          doc(db, "schools", activeSchoolId, "maintenance_reports", recordId),
          newRecord,
        );
      }

      setIsModalOpen(false);
      setFormData({ location: "", description: "" });
      setFile(null);
      setEditingId(null);
    } catch (err) {
      console.error(err);
      alert(t('maintenance.submitFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('maintenance.deleteConfirm'))) {
      const report = myReports.find(r => r.maintain_id === id);
      if (report) {
        const extractPath = (url) => {
          try {
            if (!url.includes('/o/')) return url;
            return decodeURIComponent(url.split('/o/')[1].split('?')[0]);
          } catch (e) {
            return url;
          }
        };

        try {
          if (report.photoUrl) {
            const photoPath = extractPath(report.photoUrl);
            await deleteObject(ref(storage, photoPath)).catch(e => console.warn("Failed to delete photo:", e));
          }
          if (report.quotation_files && report.quotation_files.length > 0) {
            for (const url of report.quotation_files) {
              const quotePath = extractPath(url);
              await deleteObject(ref(storage, quotePath)).catch(e => console.warn("Failed to delete quotation:", e));
            }
          }
        } catch(e) {
          console.warn("Storage deletion error:", e);
        }
      }
      try {
        await deleteDoc(
          doc(db, "schools", activeSchoolId, "maintenance_reports", id),
        );
      } catch (err) {
        console.error("Error deleting report:", err);
        alert(t('maintenance.deleteFailed'));
      }
    }
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {isModalOpen && (
        <div
          onClick={() => { if (!uploading) setIsModalOpen(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 120,
            backgroundColor: 'var(--color-overlay)',
            display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
            padding: isMobile ? 0 : '2rem',
          }}
        >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            backgroundColor: 'var(--color-bg-card)',
            borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            boxShadow: 'var(--shadow-lg)',
            width: '100%', maxWidth: isMobile ? '100%' : '820px',
            maxHeight: isMobile ? '92vh' : '90vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: '1rem', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
            <h3 style={{ fontSize: "1.125rem", fontWeight: "600", margin: 0 }}>
              {editingId ? t('maintenance.editReport') : t('maintenance.submitNewReport')}
            </h3>
            <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
              <button type="button" className="btn-outline" onClick={() => setIsModalOpen(false)} disabled={uploading}>{t('common.cancel')}</button>
              <button type="submit" className="btn-primary" form="my-report-form" disabled={uploading}>{uploading ? t('common.saving') : t('common.save')}</button>
            </div>
          </div>
          {/* Single column on a phone -- the three-column grid squeezed the location select and the
              file picker into unusable slivers. */}
          <form id="my-report-form" onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr 1fr", gap: "1.5rem", alignItems: "start", padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('maintenance.location')}</label>
              <select style={{ width: "100%", height: "40px", boxSizing: "border-box", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--color-border)", backgroundColor: "var(--color-bg-input)", color: "var(--color-text-main)" }} value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required>
                <option value="">{t('maintenance.selectLocation')}</option>
                {locations.map((loc, idx) => (
                  <option key={idx} value={loc.code}>{loc.bldg} - {loc.floor} - {loc.nameEn || loc.code}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('maintenance.description')}</label>
              <input type="text" style={{ width: "100%", height: "40px", boxSizing: "border-box", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--color-border)", backgroundColor: "var(--color-bg-input)", color: "var(--color-text-main)" }} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('maintenance.photoOptional')}</label>
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} style={{ width: "100%", height: "40px", boxSizing: "border-box", padding: "0.4rem 0.5rem", borderRadius: "4px", border: "1px solid var(--color-border)", backgroundColor: "var(--color-bg-input)", color: "var(--color-text-main)" }} />
            </div>
          </form>
        </div>
        </div>
      )}

      <div style={{ backgroundColor: "var(--color-bg-card)", borderRadius: isMobile ? 0 : "var(--radius-lg)", boxShadow: isMobile ? "none" : "var(--shadow-sm)", border: isMobile ? "none" : "1px solid var(--color-border)", overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            textAlign: "left",
          }}
        >
          <thead style={{ position: "sticky", top: 0, backgroundColor: "var(--color-bg-card)", zIndex: 10 }}>
            <tr>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)", fontSize: "0.875rem" }}>{t('maintenance.id')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)", fontSize: "0.875rem" }}>{t('maintenance.date')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)", fontSize: "0.875rem" }}>{t('maintenance.location')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)", fontSize: "0.875rem" }}>{t('maintenance.description')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)", fontSize: "0.875rem" }}>{t('maintenance.photo')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)", fontSize: "0.875rem" }}>{t('common.status')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)", fontSize: "0.875rem" }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {myReports.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                  {t('maintenance.noReports')}
                </td>
              </tr>
            ) : (
              myReports.map((r) => (
                <tr key={r.maintain_id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "1rem", fontFamily: "monospace", fontSize: "0.875rem" }}>{r.maintain_id}</td>
                  <td style={{ padding: "1rem", fontSize: "0.875rem" }}>{r.submit_date}</td>
                  <td style={{ padding: "1rem", fontSize: "0.875rem" }}>{r.location}</td>
                  <td style={{ padding: "1rem", fontSize: "0.875rem" }}>{r.description}</td>
                  <td style={{ padding: "1rem", fontSize: "0.875rem" }}>
                    {r.photoUrl ? <PhotoThumbnail url={r.photoUrl} /> : "-"}
                  </td>
                  <td style={{ padding: "1rem", fontSize: "0.875rem" }}>
                      <span
                        style={{
                          padding: "0.25rem 0.5rem",
                          borderRadius: "4px",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          backgroundColor:
                            r.status === "Submitted"
                              ? "var(--color-warning-bg)"
                              : r.status === "In-progress"
                                ? "var(--color-info-bg)"
                                : "var(--color-success-bg)",
                          color:
                            r.status === "Submitted"
                              ? "var(--color-warning-text)"
                              : r.status === "In-progress"
                                ? "var(--color-info-text)"
                                : "var(--color-success-text)",
                        }}
                      >
                        {statusWord(t, r.status)}
                      </span>
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      {r.status === "Submitted" && (
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button
                            onClick={() => handleEdit(r)}
                            className="btn-outline"
                            style={{ padding: "0.5rem" }}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(r.maintain_id)}
                            className="btn-outline"
                            style={{
                              padding: "0.5rem",
                              color: "var(--color-danger)",
                              borderColor: "var(--color-danger)",
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
  );
};

// ----------------------------------------------------------------------
// Follow-up View
// ----------------------------------------------------------------------
const FollowUpView = ({ activeSchoolId, userProfile, staff, reports }) => {
  const { t } = useT();
  const { isMobile, closeSidebar } = useOutletContext();
  const [editingRecord, setEditingRecord] = useState(null);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  // Shared dialog behaviour: retract the mobile sidebar, lock the page behind, close on Escape.
  // Escape is ignored mid-upload so a part-uploaded quotation can't be abandoned by a stray keypress.
  useEffect(() => {
    if (!editingRecord) return;
    if (isMobile) closeSidebar?.();

    const onKey = (e) => { if (e.key === 'Escape' && !uploading) { setEditingRecord(null); setFiles([]); } };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [editingRecord, isMobile, closeSidebar, uploading]);

  const handleDeleteQuotation = async (urlToRemove) => {
    if (!window.confirm(t('maintenance.deleteQuotationConfirm'))) return;
    
    setUploading(true);
    try {
      const fileRef = ref(storage, urlToRemove);
      await deleteObject(fileRef);

      const newQuotationFiles = editingRecord.quotation_files.filter((url) => url !== urlToRemove);
      await updateDoc(
        doc(db, "schools", activeSchoolId, "maintenance_reports", editingRecord.maintain_id),
        {
          quotation_files: newQuotationFiles,
          last_update: new Date().toISOString().replace("T", " ").substring(0, 19)
        }
      );

      setEditingRecord({ ...editingRecord, quotation_files: newQuotationFiles });
    } catch (err) {
      console.error(err);
      alert(t('maintenance.deleteQuotationFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let newUrls = [];
      if (files.length > 0) {
        if (editingRecord.quotation_files.length + files.length > 5) {
          alert(t('maintenance.quotationLimit'));
          setUploading(false);
          return;
        }

        const ts = getTimestampStr();
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const ext = file.name.split('.').pop();
          const qNum = String(editingRecord.quotation_files.length + i + 1).padStart(2, '0');
          const newFileName = `quote_${qNum}_${ts}.${ext}`;
          const fileRef = ref(
            storage,
            `upload/schools/${activeSchoolId}/maintenance/${editingRecord.maintain_id}/quotations/${newFileName}`,
          );
          const snapshot = await uploadBytesResumable(fileRef, file);
          const url = await getDownloadURL(snapshot.ref);
          newUrls.push(url);
        }
      }

      await updateDoc(
        doc(
          db,
          "schools",
          activeSchoolId,
          "maintenance_reports",
          editingRecord.maintain_id,
        ),
        {
          status: editingRecord.status,
          handle_by: editingRecord.handle_by,
          price: editingRecord.price,
          last_update: new Date()
            .toISOString()
            .replace("T", " ")
            .substring(0, 19),
          quotation_files: [...editingRecord.quotation_files, ...newUrls],
        },
      );

      setEditingRecord(null);
      setFiles([]);
    } catch (err) {
      console.error(err);
      alert(t('maintenance.updateFailed'));
    } finally {
      setUploading(false);
    }
  };

  const getStaffName = (email) => {
    const s = staff.find((x) => x.email === email);
    return s ? `${s.lastName} ${s.firstName}` : email;
  };

  const getStaffInitial = (email) => {
    if (!email) return "";
    const s = staff.find((x) => x.email === email);
    if (s && s.initial) return s.initial;
    if (s) return `${s.firstName?.charAt(0) || ""}${s.lastName?.charAt(0) || ""}`.toUpperCase();
    return email.split("@")[0].substring(0, 2).toUpperCase();
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {editingRecord && (
        <div
          onClick={() => { if (!uploading) { setEditingRecord(null); setFiles([]); } }}
          style={{
            position: 'fixed', inset: 0, zIndex: 120,
            backgroundColor: 'var(--color-overlay)',
            display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
            padding: isMobile ? 0 : '2rem',
          }}
        >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            backgroundColor: 'var(--color-bg-card)',
            borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            boxShadow: 'var(--shadow-lg)',
            width: '100%', maxWidth: isMobile ? '100%' : '900px',
            maxHeight: isMobile ? '92vh' : '90vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: '1rem', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
            <h3 style={{ fontSize: "1.125rem", fontWeight: "600", margin: 0 }}>
              Process Record: {editingRecord.maintain_id}
            </h3>
            <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
              <button type="button" className="btn-outline" onClick={() => { setEditingRecord(null); setFiles([]); }} disabled={uploading}>{t('common.cancel')}</button>
              <button type="submit" className="btn-primary" form="follow-up-form" disabled={uploading}>{uploading ? t('common.saving') : t('common.save')}</button>
            </div>
          </div>
          {/* Four columns collapse to one on a phone -- otherwise Status/Handle By/Price/Quotations
              each got roughly a quarter of the screen width. */}
          <form id="follow-up-form" onSubmit={handleUpdate} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1.5fr", gap: "1.5rem", alignItems: "start", padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('common.status')}</label>
              <select style={{ width: "100%", height: "40px", boxSizing: "border-box", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--color-border)", backgroundColor: "var(--color-bg-input)", color: "var(--color-text-main)" }} value={editingRecord.status} onChange={(e) => setEditingRecord({ ...editingRecord, status: e.target.value })}>
                <option value="Submitted">{t('maintenance.statuses.submitted')}</option>
                <option value="In-progress">{t('maintenance.statuses.inprogress')}</option>
                <option value="Complete">{t('maintenance.statuses.complete')}</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('maintenance.handleBy')}</label>
              <select style={{ width: "100%", height: "40px", boxSizing: "border-box", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--color-border)", backgroundColor: "var(--color-bg-input)", color: "var(--color-text-main)" }} value={editingRecord.handle_by} onChange={(e) => setEditingRecord({ ...editingRecord, handle_by: e.target.value })}>
                <option value="">{t('maintenance.selectStaff')}</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.email}>{getStaffInitial(s.email)}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('maintenance.finalPrice')}</label>
              <input type="number" step="0.01" style={{ width: "100%", height: "40px", boxSizing: "border-box", padding: "0.5rem", borderRadius: "4px", border: "1px solid var(--color-border)", backgroundColor: "var(--color-bg-input)", color: "var(--color-text-main)" }} value={editingRecord.price || ""} onChange={(e) => setEditingRecord({ ...editingRecord, price: e.target.value })} placeholder={t('maintenance.pricePlaceholder')} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('maintenance.quotationFilesCount', { count: editingRecord.quotation_files?.length || 0 })}</label>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", marginBottom: "0.5rem" }}>
                {(editingRecord.quotation_files || []).map((url, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <a href={url} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", color: "var(--color-primary)" }}>
                      <FileText size={14} /> {t('maintenance.quotationNumbered', { number: idx + 1 })}
                    </a>
                    <button 
                      type="button" 
                      onClick={() => handleDeleteQuotation(url)}
                      style={{ background: "none", border: "none", color: "var(--color-danger)", cursor: "pointer", padding: "0.2rem", display: "flex", alignItems: "center" }}
                      title={t('maintenance.deleteFile')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              {(!editingRecord.quotation_files || editingRecord.quotation_files.length < 5) && (
                <input type="file" accept=".pdf,.doc,.docx,image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files))} style={{ width: "100%", height: "40px", boxSizing: "border-box", padding: "0.4rem 0.5rem", borderRadius: "4px", border: "1px solid var(--color-border)", backgroundColor: "var(--color-bg-input)", color: "var(--color-text-main)" }} />
              )}
            </div>
          </form>
        </div>
        </div>
      )}

      <div style={{ backgroundColor: "var(--color-bg-card)", borderRadius: isMobile ? 0 : "var(--radius-lg)", boxShadow: isMobile ? "none" : "var(--shadow-sm)", border: isMobile ? "none" : "1px solid var(--color-border)", overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            textAlign: "left",
            fontSize: "0.875rem",
          }}
        >
          <thead style={{ position: "sticky", top: 0, backgroundColor: "var(--color-bg-card)", zIndex: 10 }}>
            <tr>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('maintenance.id')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('maintenance.date')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('maintenance.location')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('maintenance.issue')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('maintenance.photo')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('maintenance.submitBy')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('common.status')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('maintenance.handleBy')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('maintenance.quotationFiles')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)" }}>{t('maintenance.price')}</th>
              <th style={{ padding: "1rem", borderBottom: "1px solid var(--color-border)", fontWeight: "600", color: "var(--color-text-muted)", textAlign: "right" }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 ? (
              <tr>
                <td colSpan={11} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                  {t('maintenance.noRecords')}
                </td>
              </tr>
            ) : (
              reports.map((r) => (
                <tr key={r.maintain_id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "1rem" }}>{r.maintain_id}</td>
                  <td style={{ padding: "1rem" }}>{r.submit_date}</td>
                  <td style={{ padding: "1rem" }}>{r.location}</td>
                  <td style={{ padding: "1rem" }}>{r.description}</td>
                  <td style={{ padding: "1rem" }}>
                    {r.photoUrl ? <PhotoThumbnail url={r.photoUrl} /> : "-"}
                  </td>
                  <td style={{ padding: "1rem" }}>{getStaffInitial(r.submit_by)}</td>
                  <td style={{ padding: "1rem" }}>
                    <div
                      style={{
                        display: "inline-block",
                        padding: "0.2rem 0.4rem",
                        borderRadius: "4px",
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        backgroundColor: r.status === "Submitted" ? "var(--color-warning-bg)" : r.status === "In-progress" ? "var(--color-info-bg)" : "var(--color-success-bg)",
                        color: r.status === "Submitted" ? "var(--color-warning-text)" : r.status === "In-progress" ? "var(--color-info-text)" : "var(--color-success-text)",
                      }}
                    >
                      {statusWord(t, r.status)}
                    </div>
                  </td>
                  <td style={{ padding: "1rem" }}>{r.handle_by ? getStaffInitial(r.handle_by) : "-"}</td>
                  <td style={{ padding: "1rem" }}>
                    {r.quotation_files?.length > 0 ? (
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        {r.quotation_files.map((url, i) => (
                           <a key={i} href={url} target="_blank" rel="noreferrer" style={{ color: "var(--color-primary)" }} title={t('maintenance.quotationNumbered', { number: i + 1 })}>
                              <FileText size={18} />
                           </a>
                        ))}
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={{ padding: "1rem" }}>{r.price ? `$${r.price}` : "-"}</td>
                  <td style={{ padding: "1rem", textAlign: "right" }}>
                    <button
                        onClick={() => setEditingRecord(r)}
                        className="btn-outline"
                        style={{ padding: "0.5rem" }}
                      >
                        <Edit2 size={16} style={{ marginRight: "0.25rem" }} />{" "}
                        Process
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
  );
};

// ----------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------
const MaintenanceReport = () => {
  const { t } = useT();
  const { activeSchoolId, userProfile, user, schoolMetadata } = useOutletContext();
  const [activeTab, setActiveTab] = useState("my_report");
  const [openNewReportTrigger, setOpenNewReportTrigger] = useState(0);

  const [locations, setLocations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [settings, setSettings] = useState({ selectedPersons: [] });
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fallback for user identifier
  const userId = userProfile?.email || user?.email || "";

  const [debugLog, setDebugLog] = useState("");

  useEffect(() => {
    if (!activeSchoolId) return;

    const fetchLocations = async () => {
      try {
        const snap = await getDoc(
          doc(db, "schools", activeSchoolId, "metadata", "system_settings"),
        );
        if (snap.exists()) {
          const data = snap.data();
          let locData = [];
          if (data.locations && Array.isArray(data.locations)) {
            locData = data.locations;
          } else if (data.dropdowns && data.dropdowns.locations && Array.isArray(data.dropdowns.locations)) {
            locData = data.dropdowns.locations;
          }
          
          if (locData.length > 0) {
            setLocations(locData);
            setDebugLog(`Loaded ${locData.length} locations.`);
          } else {
            setDebugLog(`No locations array found. Keys in doc: ${Object.keys(data).join(", ")}`);
          }
        } else {
          setDebugLog("Document system_settings does not exist!");
        }
      } catch (err) {
        console.error("Error fetching locations:", err);
        setDebugLog("Error: " + err.message);
      }
    };

    const fetchStaff = async () => {
      try {
        const snap = await getDocs(
          collection(db, "schools", activeSchoolId, "staff_profiles"),
        );
        setStaff(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error fetching staff:", err);
      }
    };

    const unsubSettings = onSnapshot(
      doc(db, "schools", activeSchoolId, "metadata", "maintenance_settings"),
      (snap) => {
        if (snap.exists()) setSettings(snap.data());
      },
    );

    const unsubReports = onSnapshot(
      collection(db, "schools", activeSchoolId, "maintenance_reports"),
      (snap) => {
        // Sort by date descending
        const sorted = snap.docs
          .map((d) => ({ maintain_id: d.id, ...d.data() }))
          .sort((a, b) => new Date(b.submit_date) - new Date(a.submit_date));
        setReports(sorted);
        setLoading(false);
      },
    );

    fetchLocations();
    fetchStaff();

    return () => {
      unsubSettings();
      unsubReports();
    };
  }, [activeSchoolId]);

  const currentUserProfile = staff.find(s => s.email === userId || s.id === userId) || userProfile;
  // Prefers the staff record, as it always did -- but normalised, because that record's `role` is
  // raw CSV-import text where userProfile's has already been canonicalised in Layout.
  const isAdmin = isAdminRole(
    normaliseRole(
      currentUserProfile?.role ||
        currentUserProfile?.systemRole ||
        userProfile?.role,
    ),
  );
  const isSelectedPerson = settings.selectedPersons?.includes(userId);
  const canAccessFollowUp = isAdmin || isSelectedPerson;

  return (
    <div className="animate-fade-in new-theme" style={{ width: "100%" }}>
      {/* Tabs */}
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          <button
            className={`theme-tab ${activeTab === "my_report" ? "active" : ""}`}
            onClick={() => setActiveTab("my_report")}
          >
            <div className="theme-tab-icon">
              <ClipboardList size={20} />
            </div>
            {t('maintenance.tabs.myReport')}
          </button>

          {canAccessFollowUp && (
            <button
              className={`theme-tab ${activeTab === "follow_up" ? "active" : ""}`}
              onClick={() => setActiveTab("follow_up")}
            >
              <div className="theme-tab-icon">
                <Wrench size={20} />
              </div>
              {t('maintenance.tabs.followUp')}
            </button>
          )}

          {isAdmin && (
            <button
              className={`theme-tab ${activeTab === "settings" ? "active" : ""}`}
              onClick={() => setActiveTab("settings")}
            >
              <div className="theme-tab-icon">
                <Settings size={20} />
              </div>
              {t('maintenance.tabs.settings')}
            </button>
          )}
        </div>

        <HeaderActionsPortal>
        {activeTab === "my_report" && (
          <div
            style={{
              display: "flex",
              gap: "1rem",
              padding: "0.5rem 0",
              alignItems: "center",
              marginLeft: "auto",
            }}
          >
            <button
              className="btn-primary"
              onClick={() => setOpenNewReportTrigger((prev) => prev + 1)}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Plus size={18} /> {t('maintenance.newReport')}
            </button>
          </div>
        )}
        </HeaderActionsPortal>
      </div>

      {/* Main Content Area */}
      <div style={{ padding: "1rem 0" }}>
        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "2rem",
              color: "var(--color-text-muted)",
            }}
          >
            {t('maintenance.loadingRecords')}
          </div>
        ) : (
          <>
            {activeTab === "my_report" && (
              <MyReportView
                activeSchoolId={activeSchoolId}
                currentUserProfile={currentUserProfile}
                userId={userId}
                locations={locations}
                reports={reports}
                openNewReportTrigger={openNewReportTrigger}
                onNewReportReset={() => setOpenNewReportTrigger(0)}
                debugLog={debugLog}
              />
            )}
            {activeTab === "follow_up" && canAccessFollowUp && (
              <FollowUpView
                activeSchoolId={activeSchoolId}
                userProfile={userProfile}
                staff={staff}
                reports={reports}
              />
            )}
            {activeTab === "settings" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <ModuleEditPermissions
                  settingsDoc="maintenance_settings"
                  moduleId="maintenance_report"
                  recordLabel="maintenance settings"
                  approveLabel="dispatch and close jobs"
                />
                {isAdmin && (
                  <SettingsView
                    activeSchoolId={activeSchoolId}
                    settings={settings}
                    staff={staff}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MaintenanceReport;
